// ============================================================================
// MIRO SERVICE - frontend/src/services/miro.ts
// API calls for Miro REST API integration
// ============================================================================

import { api } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface MiroTemplateFrame {
  frameId: string;
  title: string;
  itemCount: number;
  position: { x: number; y: number };
  geometry: { width: number; height: number };
  lastScannedAt: string;
}

export interface MiroTemplateBoardConfig {
  templateBoardId: string;
  lastScannedAt: string;
  configuredBy: string;
}

export interface MiroProjectBoard {
  projectId: string;
  boardId: string;
  boardUrl: string;
  selectedTemplates: string[];
  copiedTemplateFrameIds: string[];
  createdAt: string;
}

export interface MiroSessionFrame {
  projectId: string;
  sessionId: string;
  sessionName: string;
  frameId: string;
  createdAt: string;
}

export interface MiroSyncResult {
  success: boolean;
  syncedAt: string;
  itemsCounted: {
    personas: number;
    painPoints: number;
    userStories: number;
    decisions: number;
    actions: number;
  };
  itemsCreated: number;
  itemsUpdated: number;
  errors?: string[];
}

export interface MiroSyncStatus {
  boardConnected: boolean;
  boardUrl?: string;
  frameCreated: boolean;
  frameId?: string;
  totalItemsSynced: number;
  lastPolledAt?: string;
  itemsByType: {
    personas: number;
    painPoints: number;
    userStories: number;
    decisions: number;
    actions: number;
  };
}

export interface MiroConflict {
  conflictId: string;
  projectId: string;
  entityType: string;
  entityId: string;
  miroItemId: string;
  spectrumValue: string;
  miroValue: string;
  detectedAt: string;
  status: 'unresolved' | 'resolved';
  resolvedAt?: string;
  resolvedBy?: string;
  resolution?: string;
}

export interface MiroOAuthStatus {
  connected: boolean;
  tokenValid?: boolean;
  teamId?: string;
  updatedAt?: string;
}

// ============================================================================
// OAUTH
// ============================================================================

export function getMiroAuthorizeUrl(): string {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${apiBase}/api/miro/oauth/authorize`;
}

export async function getMiroOAuthStatus(): Promise<MiroOAuthStatus> {
  const response = await api.get('/api/miro/oauth/status');
  return response.data;
}

// ============================================================================
// TEMPLATE BOARD & FRAMES
// ============================================================================

export async function getTemplateBoardConfig(): Promise<MiroTemplateBoardConfig | null> {
  const response = await api.get('/api/miro/templates/config');
  return response.data.config;
}

export async function listTemplateFrames(): Promise<MiroTemplateFrame[]> {
  const response = await api.get('/api/miro/templates/frames');
  return response.data.frames;
}

export async function scanTemplateBoard(
  templateBoardId: string
): Promise<{ status: string; requestId: string }> {
  const response = await api.post('/api/miro/templates/scan', { templateBoardId });
  return response.data;
}

// ============================================================================
// PROJECT BOARD
// ============================================================================

export async function createProjectBoard(
  projectId: string,
  projectName: string,
  selectedFrameIds: string[]
): Promise<{ status: string; requestId: string }> {
  const response = await api.post(`/api/miro/projects/${projectId}/board`, {
    projectName,
    selectedFrameIds
  });
  return response.data;
}

export async function getProjectBoard(
  projectId: string
): Promise<{ board: MiroProjectBoard | null; connected: boolean }> {
  const response = await api.get(`/api/miro/projects/${projectId}/board`);
  return response.data;
}

export async function disconnectProjectBoard(projectId: string): Promise<void> {
  await api.delete(`/api/miro/projects/${projectId}/board`);
}

export async function addTemplateFrameToBoard(
  projectId: string,
  frameId: string
): Promise<{ status: string; requestId: string }> {
  const response = await api.post(`/api/miro/projects/${projectId}/board/frames`, { frameId });
  return response.data;
}

// ============================================================================
// SESSION SYNC
// ============================================================================

export async function createSessionFrame(
  projectId: string,
  sessionId: string,
  sessionName: string
): Promise<MiroSessionFrame> {
  const response = await api.post(
    `/api/miro/projects/${projectId}/sessions/${sessionId}/frame`,
    { sessionName }
  );
  return response.data.frame;
}

export async function syncSessionToMiro(
  projectId: string,
  sessionId: string
): Promise<MiroSyncResult> {
  const response = await api.post(
    `/api/miro/projects/${projectId}/sessions/${sessionId}/sync`
  );
  return response.data.result;
}

export async function getSyncStatus(
  projectId: string,
  sessionId: string
): Promise<MiroSyncStatus> {
  const response = await api.get(
    `/api/miro/projects/${projectId}/sessions/${sessionId}/status`
  );
  return response.data.status;
}

// ============================================================================
// POLLING / CONFLICTS
// ============================================================================

export async function pollBoardChanges(
  projectId: string
): Promise<{ newConflicts: number; conflicts: MiroConflict[] }> {
  const response = await api.post(`/api/miro/projects/${projectId}/poll`);
  return response.data;
}

export async function listConflicts(
  projectId: string,
  unresolvedOnly: boolean = true
): Promise<MiroConflict[]> {
  const response = await api.get(
    `/api/miro/projects/${projectId}/conflicts?unresolvedOnly=${unresolvedOnly}`
  );
  return response.data.conflicts;
}

export async function resolveConflict(
  projectId: string,
  conflictId: string,
  resolution: 'keep-spectrum' | 'keep-miro'
): Promise<void> {
  await api.post(`/api/miro/projects/${projectId}/conflicts/${conflictId}/resolve`, {
    resolution
  });
}

// ============================================================================
// FRAME INGESTION
// ============================================================================

export interface MiroBoardFrame {
  frameId: string;
  title: string;
  position?: { x: number; y: number };
  geometry?: { width: number; height: number };
}

export interface MiroIngestResult {
  framesProcessed: number;
  taskIds: string[];
  message: string;
}

export async function listBoardFrames(projectId: string): Promise<MiroBoardFrame[]> {
  const response = await api.get(`/api/miro/projects/${projectId}/board/frames/list`);
  return response.data.frames || [];
}

export async function ingestFrames(
  projectId: string,
  frameIds?: string[],
  entireBoard?: boolean
): Promise<MiroIngestResult> {
  const response = await api.post(`/api/miro/projects/${projectId}/ingest`, {
    frameIds,
    entireBoard
  });
  return response.data;
}
